import type {
  BackendMcpIneligibleReason,
  BackendOAuthConsentRequest,
  BackendOAuthConsentWorkspace,
} from 'src/types/backend';

export interface OAuthConsentCustomerOption {
  id: string;
  name: string;
  eligibleWorkspaces: BackendOAuthConsentWorkspace[];
  ineligibleReason: BackendMcpIneligibleReason | null;
}

export interface OAuthConsentWorkspaceSelection {
  selectedCustomerId: string | null;
  selectedWorkspaceId: string | null;
}

export const getEligibleWorkspaces = (
  workspaces: BackendOAuthConsentWorkspace[]
): BackendOAuthConsentWorkspace[] => workspaces.filter((workspace) => workspace.eligible);

// A customer without an eligible workspace keeps the reason, so the picker can say why.
export const groupWorkspacesByCustomer = (
  workspaces: BackendOAuthConsentWorkspace[]
): OAuthConsentCustomerOption[] => {
  const options = new Map<string, OAuthConsentCustomerOption>();

  workspaces.forEach((workspace) => {
    const option = options.get(workspace.customer.id) ?? {
      id: workspace.customer.id,
      name: workspace.customer.name,
      eligibleWorkspaces: [],
      ineligibleReason: null,
    };

    if (workspace.eligible) {
      option.eligibleWorkspaces.push(workspace);
    } else {
      option.ineligibleReason = option.ineligibleReason ?? workspace.ineligibleReason ?? null;
    }

    options.set(option.id, option);
  });

  return [...options.values()].map((option) => ({
    ...option,
    ineligibleReason: option.eligibleWorkspaces.length > 0 ? null : option.ineligibleReason,
  }));
};

export const getEligibleCustomers = (
  workspaces: BackendOAuthConsentWorkspace[]
): OAuthConsentCustomerOption[] =>
  groupWorkspacesByCustomer(workspaces).filter((option) => option.eligibleWorkspaces.length > 0);

export const getOnlyWorkspaceId = (workspaces: BackendOAuthConsentWorkspace[]): string | null =>
  workspaces.length === 1 ? workspaces[0].id : null;

export const getInitialWorkspaceSelection = ({
  workspaces,
  selectedWorkspaceId,
}: BackendOAuthConsentRequest): OAuthConsentWorkspaceSelection => {
  const preselected = getEligibleWorkspaces(workspaces).find(
    (workspace) => workspace.id === selectedWorkspaceId
  );
  if (preselected) {
    return { selectedCustomerId: preselected.customer.id, selectedWorkspaceId: preselected.id };
  }

  const eligibleCustomers = getEligibleCustomers(workspaces);
  if (eligibleCustomers.length === 1) {
    const [customer] = eligibleCustomers;
    return {
      selectedCustomerId: customer.id,
      selectedWorkspaceId: getOnlyWorkspaceId(customer.eligibleWorkspaces),
    };
  }

  return { selectedCustomerId: null, selectedWorkspaceId: null };
};
